import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, Check, Flag, RotateCcw, X } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { AskAssistantButton } from "@/components/assistant/AskAssistantButton"
import { HelpfulPrompt } from "@/components/quizzes/HelpfulPrompt"
import { AreaBreakdown } from "@/components/quizzes/AreaBreakdown"
import { CHOICE_LETTERS } from "@/components/quizzes/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { ExamAttemptSummary, ReviewQuestion } from "@/types/exams"
import type { AreaResult } from "@/types/quizzes"

type Filter = "all" | "missed" | "flagged"

export default function ExamResults({
  attempt,
  exam,
  areas,
  cases,
}: {
  attempt: ExamAttemptSummary & { correct_count: number }
  exam: { id: number; title: string }
  areas: AreaResult[]
  cases: { id: number; title: string | null; questions: ReviewQuestion[] }[]
}) {
  const intl = useIntl()
  const format = useFormat()
  const [filter, setFilter] = React.useState<Filter>("all")
  const full = attempt.mode === "full_exam"
  const title = intl.formatMessage({ id: "exams.results_title" }, { title: exam.title })
  const description = intl.formatMessage({ id: "exams.results_meta" }, { title: exam.title })
  const all = cases.flatMap((c) => c.questions)
  const missedCount = all.filter((q) => !q.correct).length
  const flaggedCount = all.filter((q) => q.flagged).length
  let number = 0

  const retake = () =>
    router.post(`/practice_exams/${exam.id}/exam_attempts`, full ? {} : { case_study_id: cases[0]?.id })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href={`/practice_exams/${exam.id}`} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {exam.title}
        </Link>
        <PageHeader
          title={intl.formatMessage({ id: "exams.results" })}
          description={
            <>
              <Badge tone={full ? "accent" : "muted"} className="mr-2">
                {full ? intl.formatMessage({ id: "exams.full_exam" }) : intl.formatMessage({ id: "exams.case_badge" }, { title: attempt.case_title })}
              </Badge>
              {exam.title}{attempt.finished_on && ` · ${format.date(attempt.finished_on, { month: "short", day: "numeric", year: "numeric" })}`}
            </>
          }
        />

        <div className="callout mt-8 text-center">
          <h2 className="text-5xl font-semibold">{format.number((attempt.score ?? 0) / 100, { style: "percent" })}</h2>
          <p className="mt-1">
            {full
              ? intl.formatMessage(
                  { id: "exams.score_full" },
                  {
                    correct: attempt.correct_count,
                    questions: format.questionCount(attempt.question_count),
                    duration: format.duration(attempt.time_used_seconds),
                  },
                )
              : intl.formatMessage(
                  { id: "exams.score_case" },
                  { correct: attempt.correct_count, questions: format.questionCount(attempt.question_count) },
                )}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button type="button" onClick={retake}>
              <RotateCcw className="h-4 w-4" /> {full ? intl.formatMessage({ id: "exams.retake_exam" }) : intl.formatMessage({ id: "exams.retake_case" })}
            </Button>
          </div>
        </div>

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "exams.by_area" })}</h2>
          <AreaBreakdown areas={areas} />
        </section>

        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2>{intl.formatMessage({ id: "exams.review_title" })}</h2>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={intl.formatMessage({ id: "exams.show_group" })}>
              {(
                [
                  ["all", intl.formatMessage({ id: "exams.filter_all" }, { count: all.length })],
                  ["missed", intl.formatMessage({ id: "exams.filter_missed" }, { count: missedCount })],
                  ["flagged", intl.formatMessage({ id: "exams.filter_flagged" }, { count: flaggedCount })],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="toggle-button"
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {cases.map((c) => {
            const numbered = c.questions.map((q) => ({ q, n: ++number }))
            const shown = numbered.filter(
              ({ q }) => filter === "all" || (filter === "missed" ? !q.correct : q.flagged),
            )
            if (shown.length === 0) return null
            return (
              <div key={c.id} className="mt-6">
                <h3>{c.title}</h3>
                <ol className="mt-3 space-y-3 p-0">
                  {shown.map(({ q, n }) => (
                    <ReviewItem key={q.id} question={q} number={n} attemptId={attempt.id} />
                  ))}
                </ol>
              </div>
            )
          })}
          {filter !== "all" && (filter === "missed" ? missedCount : flaggedCount) === 0 && (
            <p className="mt-4">{filter === "missed" ? intl.formatMessage({ id: "quizzes.none_missed" }) : intl.formatMessage({ id: "exams.none_flagged" })}</p>
          )}
        </section>
      </AppShell>
    </>
  )
}

function ReviewItem({ question, number, attemptId }: { question: ReviewQuestion; number: number; attemptId: number }) {
  const intl = useIntl()
  const answered = question.chosen_choice !== null
  return (
    <li className="list-none rounded-xl border border-hairline bg-page p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-full",
            question.correct ? "bg-accent-faded text-accent-display" : "bg-danger-faded text-danger-display",
          )}
          aria-label={question.correct ? intl.formatMessage({ id: "exams.correct" }) : intl.formatMessage({ id: "exams.incorrect" })}
        >
          {question.correct ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
        </span>
        <span className="text-sm text-ink-muted">{intl.formatMessage({ id: "exams.question_n" }, { number })}</span>
        {question.content_area_name && <Badge tone="muted">{question.content_area_name}</Badge>}
        {question.flagged && (
          <Badge tone="signal">
            <Flag className="h-3 w-3" /> {intl.formatMessage({ id: "exams.flagged" })}
          </Badge>
        )}
      </div>
      <p className="mt-3 whitespace-pre-wrap break-words">
        <strong>{question.text}</strong>
      </p>
      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex gap-2">
          <dt className="shrink-0 text-ink-muted">{intl.formatMessage({ id: "quizzes.your_answer" })}</dt>
          <dd className={cn("min-w-0 break-words", question.correct ? "text-accent-display" : "text-danger-display")}>
            {answered
              ? `${CHOICE_LETTERS[question.chosen_choice!]}. ${question.choices[question.chosen_choice!]}`
              : intl.formatMessage({ id: "exams.not_answered" })}
          </dd>
        </div>
        {!question.correct && (
          <div className="flex gap-2">
            <dt className="shrink-0 text-ink-muted">{intl.formatMessage({ id: "quizzes.correct_answer" })}</dt>
            <dd className="min-w-0 break-words text-accent-display">
              {CHOICE_LETTERS[question.correct_choice]}. {question.choices[question.correct_choice]}
            </dd>
          </div>
        )}
      </dl>
      {question.explanation && <p className="mt-3 whitespace-pre-wrap">{question.explanation}</p>}
      <div className="mt-3">
        <HelpfulPrompt key={question.id} attemptId={attemptId} questionId={question.id} />
      </div>
      <div className="mt-3">
        <AskAssistantButton attemptId={attemptId} questionId={question.id} />
      </div>
    </li>
  )
}
